/**
 * ApprovalRuleDialog - create/edit a single registration ApprovalRule.
 *
 * Mirrors ValidationRuleDialog (same ConditionBuilder + severity model)
 * but swaps the trigger_step field for the approval-specific controls:
 * target_status, required_reason_codes (multi-select), resolver_roles
 * (multi-select).
 *
 * Form state is owned by the canonical `useForm` helper from
 * @/shared/lib/forms (see CLAUDE.md - Editable Detail Cards / form
 * generation rules). Multi-select pickers and the predicate editor are
 * embedded via the `custom` field type so validation still flows
 * through the form schema.
 */

import { useMemo, useState } from 'react'
import { Check, X } from 'lucide-react'
import { toast } from 'sonner'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Switch } from '@/shared/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { ConditionBuilder } from '@/admin/components/condition-builder'
import { useAvailableRefs } from '@/admin/components/condition-builder/use-available-refs'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import {
  useCreateApprovalRule,
  useUpdateApprovalRule,
  type ApprovalRule,
  type ApprovalTargetStatus,
} from '@/admin/hooks/use-approval-rules'
import type { Severity } from '@/admin/hooks/use-validation-rules'
import { useOrg } from '@/admin/hooks/use-org'
import api from '@/admin/lib/api'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'

import { toastApiError } from '@/shared/lib/api-error'
const SEVERITIES: { value: Severity; label: string }[] = [
  { value: 'BLOCK', label: 'Block - require approval' },
  { value: 'WARN', label: 'Warn - notify approver' },
  { value: 'INFO', label: 'Info - informational only' },
]

const TARGET_STATUSES: { value: ApprovalTargetStatus; label: string }[] = [
  { value: 'pending_approval', label: 'Pending approval (stay in draft)' },
]

interface OrgRole {
  uuid: string
  name: string
}

interface ApprovalRuleDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  rule: ApprovalRule | null
}

type RuleFormValues = {
  name: string
  conditions: PredicateNode | null
  severity: Severity
  message: string
  target_status: ApprovalTargetStatus
  required_reason_codes: string[]
  resolver_roles: string[]
  is_active: boolean
}

export function ApprovalRuleDialog({
  open,
  onOpenChange,
  rule,
}: ApprovalRuleDialogProps) {
  const { refs, byRef, isLoading: refsLoading } = useAvailableRefs()
  const { data: org } = useOrg()
  const create = useCreateApprovalRule()
  const update = useUpdateApprovalRule()

  const orgUuid = org?.organisation?.uuid
  const { data: rolesData, isLoading: rolesLoading } = useQuery<OrgRole[]>({
    queryKey: ['org-roles', orgUuid],
    queryFn: () =>
      api.get(`/organisations/${orgUuid}/roles/`).then((r) => {
        const data = r.data?.results ?? r.data
        return Array.isArray(data) ? data : []
      }),
    enabled: !!orgUuid && open,
    staleTime: 60_000,
  })
  const roles = useMemo(() => rolesData ?? [], [rolesData])
  const roleItems = useMemo(
    () => roles.map((role) => ({ uuid: role.uuid, label: role.name })),
    [roles],
  )

  const initialValues: RuleFormValues = useMemo(
    () => ({
      name: rule?.name ?? '',
      conditions: rule?.conditions ?? null,
      severity: rule?.severity ?? 'BLOCK',
      message: rule?.message ?? '',
      target_status: rule?.target_status ?? 'pending_approval',
      required_reason_codes: rule ? [...rule.required_reason_codes] : [],
      resolver_roles: rule ? [...rule.resolver_roles] : [],
      is_active: rule?.is_active ?? true,
    }),
    [rule],
  )

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'name',
        label: 'Name',
        testId: 'approval-rule-name',
        placeholder: 'Late registration (90-day window)',
        className: 'col-span-2',
        value: initialValues.name,
        validationSchema: z.string().trim().min(1, 'Rule name is required'),
      },
      {
        type: 'custom',
        name: 'conditions',
        className: 'col-span-2',
        value: initialValues.conditions,
        validationSchema: z.any()
          .nullable()
          .refine(
            (value) => value !== null && value !== undefined,
            { message: 'Add at least one condition with a field, operator, and value' },
          ),
        render: (formContext) => {
          const value = (formContext.values.conditions ?? null) as PredicateNode | null
          const error = formContext.errors.conditions as string | undefined
          return (
            <div className="col-span-2 flex flex-col gap-1.5">
              <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Conditions
              </Label>
              {refsLoading ? (
                <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-4 text-center text-[12px] text-[var(--ink-3)]">
                  Loading fields…
                </div>
              ) : (
                <ConditionBuilder
                  value={value}
                  onChange={(next) => formContext.setFieldValue('conditions', next)}
                  availableRefs={refs}
                  byRef={byRef}
                />
              )}
              {error && (
                <p className="text-[11px] text-[var(--status-error)]">{error}</p>
              )}
            </div>
          )
        },
      },
      {
        type: 'select',
        name: 'severity',
        label: 'Severity',
        testId: 'approval-rule-severity',
        className: 'col-span-1',
        value: initialValues.severity,
        options: SEVERITIES,
      },
      {
        type: 'text',
        name: 'message',
        label: 'Message',
        testId: 'approval-rule-message',
        placeholder: 'Shown to the approver when this rule matches',
        className: 'col-span-1',
        value: initialValues.message,
        validationSchema: z.string().trim().min(1, 'Rule message is required'),
      },
      {
        type: 'select',
        name: 'target_status',
        label: 'Target status',
        testId: 'approval-rule-target-status',
        className: 'col-span-2',
        value: initialValues.target_status,
        options: TARGET_STATUSES,
      },
      {
        // The backend stores required_reason_codes as a free-form JSON
        // list of slugs (see ``ApprovalRule.required_reason_codes``).
        // The legacy ``ExceptionReason`` catalog has been removed, so
        // we render a simple slug input - the rule author types the
        // slugs an approver may resolve with (e.g. ``late_install``,
        // ``ho_request``).
        type: 'custom',
        name: 'required_reason_codes',
        className: 'col-span-2',
        value: initialValues.required_reason_codes,
        render: (formContext) => {
          const selected =
            (formContext.values.required_reason_codes as string[]) ?? []
          return (
            <SlugListInput
              label="Required reason codes"
              hint="Slugs the approver must pick from when resolving this exception. Press Enter or comma to add."
              testId="approval-rule-reasons"
              value={selected}
              onChange={(next) =>
                formContext.setFieldValue('required_reason_codes', next)
              }
            />
          )
        },
      },
      {
        type: 'custom',
        name: 'resolver_roles',
        className: 'col-span-2',
        value: initialValues.resolver_roles,
        render: (formContext) => {
          const selected = (formContext.values.resolver_roles as string[]) ?? []
          return (
            <MultiSelectList
              label="Resolver roles"
              empty={
                rolesLoading ? 'Loading roles…' : 'No roles in this organisation yet.'
              }
              items={roleItems}
              selected={selected}
              onToggle={(uuid) => {
                const has = selected.includes(uuid)
                formContext.setFieldValue(
                  'resolver_roles',
                  has ? selected.filter((id) => id !== uuid) : [...selected, uuid],
                )
              }}
              testId="approval-rule-roles"
            />
          )
        },
      },
      {
        type: 'custom',
        name: 'is_active',
        className: 'col-span-2',
        value: initialValues.is_active,
        render: (formContext) => {
          const active = Boolean(formContext.values.is_active)
          return (
            <Button
              variant="ghost"
              type="button"
              onClick={() => formContext.setFieldValue('is_active', !active)}
              className="col-span-2 h-auto w-full justify-start gap-3 whitespace-normal rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-left hover:border-[var(--green-border)]"
            >
              <Switch checked={active} decorative />
              <div>
                <span className="block text-[12px] text-[var(--ink)]">
                  {active ? 'Active' : 'Inactive'}
                </span>
                <span className="mt-0.5 block text-[11px] text-[var(--ink-3)]">
                  Inactive rules are retained but never evaluated at submission.
                </span>
              </div>
            </Button>
          )
        },
      },
    ],
    [
      byRef,
      initialValues.conditions,
      initialValues.is_active,
      initialValues.message,
      initialValues.name,
      initialValues.required_reason_codes,
      initialValues.resolver_roles,
      initialValues.severity,
      initialValues.target_status,
      refs,
      refsLoading,
      roleItems,
      rolesLoading,
    ],
  )

  const { Form, FormFooter } = useForm(
    {
      name: 'approval-rule-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as RuleFormValues
      const payload = {
        name: values.name.trim(),
        description: '',
        conditions: values.conditions,
        severity: values.severity,
        message: values.message.trim(),
        target_status: values.target_status,
        required_reason_codes: values.required_reason_codes,
        resolver_roles: values.resolver_roles,
        is_active: values.is_active,
      }
      try {
        if (rule) {
          await update.mutateAsync({ uuid: rule.uuid, ...payload })
          toast.success('Rule updated')
        } else {
          await create.mutateAsync(payload)
          toast.success('Rule created')
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, 'Failed to save rule');
      }
    },
    [open, rule?.uuid, fields],
  )

  const busy = create.isPending || update.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{rule ? 'Edit approval rule' : 'New approval rule'}</DialogTitle>
          <DialogDescription>
            When the conditions match, registration is held for approval. Pick
            the reason codes an approver may resolve with, plus the roles
            allowed to approve.
          </DialogDescription>
        </DialogHeader>
        <Form className="grid grid-cols-2 gap-4 py-2" />
        <DialogFooter>
          {FormFooter && (
            <FormFooter
              onCancel={() => onOpenChange(false)}
              submitText={busy ? 'Saving…' : rule ? 'Save changes' : 'Create rule'}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface MultiItem {
  uuid: string
  label: string
  sub?: string
}

interface MultiSelectListProps {
  label: string
  empty: string
  items: MultiItem[]
  selected: string[]
  onToggle: (uuid: string) => void
  testId?: string
}

function MultiSelectList({
  label,
  empty,
  items,
  selected,
  onToggle,
  testId,
}: MultiSelectListProps) {
  return (
    <div className="col-span-2 flex flex-col gap-1.5">
      <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
        {label}
      </Label>
      {items.length === 0 ? (
        <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2 text-[12px] text-[var(--ink-3)]">
          {empty}
        </div>
      ) : (
        <div
          data-testid={testId}
          className="flex flex-col gap-px overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]"
        >
          {items.map((item) => {
            const active = selected.includes(item.uuid)
            return (
              <Button
                key={item.uuid}
                type="button"
                variant="ghost"
                onClick={() => onToggle(item.uuid)}
                className={`h-auto w-full justify-start gap-3 rounded-none border-0 px-3 py-2 text-left ${
                  active
                    ? 'bg-[var(--green-soft)] text-[var(--ink)]'
                    : 'bg-[var(--bg)] text-[var(--ink-2)]'
                }`}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border-[0.5px] ${
                    active
                      ? 'border-[var(--green)] bg-[var(--green)] text-white'
                      : 'border-[var(--rule-strong)] bg-[var(--bg)]'
                  }`}
                >
                  {active && <Check size={10} />}
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="text-[12px]">{item.label}</span>
                  {item.sub && (
                    <span className="text-[10px] text-[var(--ink-3)]">{item.sub}</span>
                  )}
                </span>
              </Button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── SlugListInput ─────────────────────────────────────────
//
// Small chip-style input for editing a list of free-form slugs (used
// for ``required_reason_codes``). Slugs are added on Enter or comma
// and normalised to ``lower_snake_case`` so they round-trip cleanly
// through the JSON field on ``ApprovalRule``.

interface SlugListInputProps {
  label: string
  hint?: string
  value: string[]
  onChange: (next: string[]) => void
  testId?: string
}

function normaliseSlug(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_:-]/g, '')
}

function SlugListInput({
  label,
  hint,
  value,
  onChange,
  testId,
}: SlugListInputProps) {
  const [draft, setDraft] = useState('')

  function commit(raw: string) {
    const slug = normaliseSlug(raw)
    if (!slug || value.includes(slug)) {
      setDraft('')
      return
    }
    onChange([...value, slug])
    setDraft('')
  }

  return (
    <div className="col-span-2 flex flex-col gap-1.5">
      <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
        {label}
      </Label>
      <div
        data-testid={testId}
        className="flex flex-wrap items-center gap-1.5 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-2 py-1.5"
      >
        {value.map((slug) => (
          <span
            key={slug}
            className="inline-flex items-center gap-1 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[11px] text-[var(--ink)]"
          >
            <span className="font-mono">{slug}</span>
            <button
              type="button"
              onClick={() => onChange(value.filter((s) => s !== slug))}
              className="text-[var(--ink-3)] transition-colors hover:text-[var(--status-error)]"
              aria-label={`Remove ${slug}`}
            >
              <X size={10} />
            </button>
          </span>
        ))}
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault()
              commit(draft)
            } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
              onChange(value.slice(0, -1))
            }
          }}
          onBlur={() => {
            if (draft.trim()) commit(draft)
          }}
          placeholder={value.length === 0 ? 'late_install, ho_request…' : ''}
          className="h-6 min-w-[120px] flex-1 border-0 bg-transparent p-0 text-[12px] shadow-none focus-visible:ring-0"
        />
      </div>
      {hint && (
        <p className="text-[10px] text-[var(--ink-3)]">{hint}</p>
      )}
    </div>
  )
}
